import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SITE_CONFIG, NAVER_BOOKING_URL } from '../data/siteConfig';
import { useAuth } from '../contexts/AuthContext';
import { AWS_CONFIG } from '../config/aws';
import NicknameModal from './NicknameModal';
import OfficialPriceModal from './OfficialPriceModal';
import NavLinkItem from './NavLinkItem';

// Public links shown to all visitors.
// '서비스&가격' 분기 (2026-05-26):
//   · 홈에서 클릭 → #pricing 섹션 스크롤
//   · 다른 페이지에서 클릭 → OfficialPriceModal 팝업 (페이지 이동 없음)
// 고객 온라인 예약 동선은 제거됨 (2026-05-22). 딜러 전용도 제거됨 (관리자 문의만 유지).
// 2026-05-27: 외부 네이버 예약(NAVER_BOOKING_URL) 메뉴 신규 추가 — 자체 예약 코드 제거는 별도 task.
// 2026-06-01: 'Blog' 메뉴는 노출이 약해 홈 히어로 '네이버 예약 하기' 버튼 아래로 이동(HeroBanner). NavBar에서 제거.
const PUBLIC_NAV_LINKS = [
  { to: '/', label: '홈' },
  { label: '예약', external: true, emphasize: true, href: NAVER_BOOKING_URL },
  { label: '서비스&가격', action: 'pricing' },
  { to: '/community', label: '커뮤니티' },
  { to: '/directions', label: '오시는길' },
];

const FAQ_CACHE_KEY = 'koa:hasFaq';
const FAQ_CACHE_UNTIL_KEY = 'koa:hasFaq:until';

export default function NavBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hasFaq, setHasFaq] = useState(false);
  const [nickModalOpen, setNickModalOpen] = useState(false);
  const [officialPriceOpen, setOfficialPriceOpen] = useState(false);
  const location = useLocation();
  const { user, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => { await signOut(); navigate('/'); };

  // 분기: 홈이면 요금표 섹션 스크롤, 그 외엔 팝업
  const handlePricingClick = () => {
    if (location.pathname === '/') {
      navigate('/#pricing');
    } else {
      setOfficialPriceOpen(true);
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // FAQ 존재 여부 체크 (5분 sessionStorage 캐시)
  useEffect(() => {
    async function checkFaq() {
      try {
        const until = parseInt(sessionStorage.getItem(FAQ_CACHE_UNTIL_KEY) || '0', 10);
        if (Date.now() < until) {
          setHasFaq(sessionStorage.getItem(FAQ_CACHE_KEY) === '1');
          return;
        }
        const res = await fetch(`${AWS_CONFIG.apiUrl}/faqs`);
        const data = await res.json();
        const has = Array.isArray(data.faqs) && data.faqs.length > 0;
        sessionStorage.setItem(FAQ_CACHE_KEY, has ? '1' : '0');
        sessionStorage.setItem(FAQ_CACHE_UNTIL_KEY, String(Date.now() + 5 * 60 * 1000));
        setHasFaq(has);
      } catch (err) {
        console.error('[NavBar:checkFaq] error', err);
      }
    }
    checkFaq();
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location]);

  // Final order: 홈 / 예약 / 서비스&가격 / 커뮤니티 / [자주 묻는 질문 if hasFaq] / 오시는길.
  // Slice-based to avoid index drift; FAQ goes before 오시는길 (index 4).
  const navLinks = [
    ...PUBLIC_NAV_LINKS.slice(0, 4),
    ...(hasFaq ? [{ to: '/faq', label: '자주 묻는 질문' }] : []),
    ...PUBLIC_NAV_LINKS.slice(4),
  ];

  const navBg = scrolled
    ? 'rgba(17, 34, 64, 0.95)'
    : 'transparent';

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      background: navBg, backdropFilter: scrolled ? 'blur(8px)' : 'none',
      transition: 'background 0.3s',
      borderBottom: scrolled ? '1px solid var(--border)' : 'none',
    }}>
      <div style={{
        maxWidth: 'var(--max-width)', margin: '0 auto',
        padding: '0 20px', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between',
        height: 64,
      }}>
        {/* Logo */}
        <Link to="/" style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: '1.25rem', fontWeight: 700,
          color: 'var(--primary)', textDecoration: 'none',
        }}>
          <img
            src="/koa-logo.png"
            alt="KOA DETAILING 로고"
            width={32}
            height={32}
            style={{ display: 'block' }}
          />
          {SITE_CONFIG.name}
        </Link>

        {/* Desktop links */}
        <div style={{
          display: 'flex', gap: 28, alignItems: 'center',
        }} className="nav-desktop">
          {navLinks.map(l => (
            <NavLinkItem
              key={l.label}
              link={l}
              variant="desktop"
              onPricingClick={handlePricingClick}
            />
          ))}
          {isAdmin && (
            <Link to="/admin" style={{
              color: 'var(--accent)', fontSize: '0.95rem', fontWeight: 500,
            }}>관리자</Link>
          )}
          {!user && (
            <Link to="/login" className="btn btn-outline" style={{
              fontSize: '0.85rem', padding: '6px 14px', textDecoration: 'none',
            }}>로그인 · 가입</Link>
          )}
          {user && (
            <button
              type="button"
              onClick={() => setNickModalOpen(true)}
              style={{
                background: 'none', border: 'none',
                color: 'var(--text-muted)', cursor: 'pointer',
                fontSize: '0.85rem', padding: '6px 10px',
              }}
            >
              닉네임
            </button>
          )}
          {user && (
            <button onClick={handleLogout} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', fontSize: '0.95rem', padding: 0,
            }}>로그아웃</button>
          )}
        </div>

        {/* Mobile hamburger */}
        <button className="nav-hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="메뉴 열기"
          style={{
            display: 'none', flexDirection: 'column', gap: 5,
            background: 'none', border: 'none', cursor: 'pointer',
          }}>
          <span style={{
            width: 24, height: 2, background: 'var(--text)',
            transition: 'transform 0.2s',
            transform: menuOpen ? 'rotate(45deg) translateY(7px)' : 'none',
          }} />
          <span style={{
            width: 24, height: 2, background: 'var(--text)',
            opacity: menuOpen ? 0 : 1, transition: 'opacity 0.2s',
          }} />
          <span style={{
            width: 24, height: 2, background: 'var(--text)',
            transition: 'transform 0.2s',
            transform: menuOpen ? 'rotate(-45deg) translateY(-7px)' : 'none',
          }} />
        </button>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="nav-mobile-menu" style={{
          background: 'rgba(17, 34, 64, 0.98)',
          padding: '16px 20px', display: 'flex',
          flexDirection: 'column', gap: 16,
          borderBottom: '1px solid var(--border)',
        }}>
          {navLinks.map(l => (
            <NavLinkItem
              key={l.label}
              link={l}
              variant="mobile"
              onPricingClick={handlePricingClick}
              onClose={() => setMenuOpen(false)}
            />
          ))}
          {isAdmin && (
            <Link to="/admin" style={{ color: 'var(--accent)' }}>관리자</Link>
          )}
          {!user && (
            <Link to="/login" style={{ color: 'var(--text-muted)' }}>로그인</Link>
          )}
          {user && (
            <button
              type="button"
              onClick={() => setNickModalOpen(true)}
              style={{
                background: 'none', border: 'none',
                color: 'var(--text-muted)', cursor: 'pointer',
                fontSize: '0.85rem', padding: '6px 10px',
                textAlign: 'left',
              }}
            >
              닉네임
            </button>
          )}
          {user && (
            <button onClick={handleLogout} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', fontSize: '1.1rem', padding: 0, textAlign: 'left',
            }}>로그아웃</button>
          )}
        </div>
      )}

      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 768px) {
          .nav-desktop { display: none !important; }
          .nav-hamburger { display: flex !important; }
        }
        @media (min-width: 769px) {
          .nav-mobile-menu { display: none !important; }
        }
      `}</style>

      <NicknameModal
        open={nickModalOpen}
        onClose={() => setNickModalOpen(false)}
      />
      <OfficialPriceModal
        open={officialPriceOpen}
        onClose={() => setOfficialPriceOpen(false)}
      />
    </nav>
  );
}
